import { Suspense, useRef, useEffect } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import {
  OrbitControls,
  useGLTF,
  useFBX,
  Html,
  Environment,
  ContactShadows,
  Bounds,
} from '@react-three/drei';
import * as THREE from 'three';
import type { Model3D } from '@/types';

interface ModelProps {
  model: Model3D;
  onLoaded: () => void;
}

function GLTFModel({ model, onLoaded }: ModelProps) {
  const mixerRef = useRef<THREE.AnimationMixer | null>(null);
  const groupRef = useRef<THREE.Group>(null);
  const gltf = useGLTF(model.path);

  useEffect(() => {
    const scene = gltf.scene;

    // Enable shadows and enhance materials for PBR
    scene.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = true;
        child.receiveShadow = true;
        if (child.material) {
          // Enhance material for better IBL response
          if (child.material instanceof THREE.MeshStandardMaterial) {
            child.material.envMapIntensity = 1.5;
            child.material.needsUpdate = true;
          }
        }
      }
    });

    // Setup animation - use the original scene, not a clone
    if (model.hasAnimations && gltf.animations && gltf.animations.length > 0) {
      mixerRef.current = new THREE.AnimationMixer(scene);
      const action = mixerRef.current.clipAction(gltf.animations[0]);
      action.play();
    }

    return () => {
      mixerRef.current?.stopAllAction();
      mixerRef.current = null;
    };
  }, [gltf, model.hasAnimations, model.path]);

  // Calculate bounding box and center model at origin, grounded on y=0
  useEffect(() => {
    const group = groupRef.current;
    if (!group) return;

    // Wait a frame for the model to be properly added
    requestAnimationFrame(() => {
      // Calculate bounding box
      const box = new THREE.Box3().setFromObject(group);
      const center = box.getCenter(new THREE.Vector3());
      const minY = box.min.y;

      // Center model horizontally and ground it at y=0
      group.position.x = -center.x;
      group.position.z = -center.z;
      group.position.y = -minY;

      onLoaded();
    });
  }, [gltf, onLoaded]);

  // Update animation on every frame using clock delta
  useFrame((_, delta) => {
    if (mixerRef.current) {
      mixerRef.current.update(delta);
    }
  });

  const scale = model.scale ?? 1;
  const rotation = model.rotation ?? [0, 0, 0];

  return (
    <group ref={groupRef}>
      <primitive
        object={gltf.scene}
        scale={scale}
        rotation={rotation}
      />
    </group>
  );
}

function FBXModel({ model, onLoaded }: ModelProps) {
  const mixerRef = useRef<THREE.AnimationMixer | null>(null);
  const groupRef = useRef<THREE.Group>(null);
  const fbx = useFBX(model.path);

  useEffect(() => {
    // Enable shadows and enhance materials for PBR
    fbx.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = true;
        child.receiveShadow = true;
        if (child.material) {
          if (child.material instanceof THREE.MeshStandardMaterial) {
            child.material.envMapIntensity = 1.5;
            child.material.needsUpdate = true;
          }
        }
      }
    });

    // Setup animation - use the original fbx object, not a clone
    if (model.hasAnimations && fbx.animations && fbx.animations.length > 0) {
      mixerRef.current = new THREE.AnimationMixer(fbx);
      const action = mixerRef.current.clipAction(fbx.animations[0]);
      action.play();
    }

    return () => {
      mixerRef.current?.stopAllAction();
      mixerRef.current = null;
    };
  }, [fbx, model.hasAnimations, model.path]);

  // Calculate bounding box and center model at origin, grounded on y=0
  useEffect(() => {
    const group = groupRef.current;
    if (!group) return;

    // Wait a frame for the model to be properly added
    requestAnimationFrame(() => {
      // Calculate bounding box
      const box = new THREE.Box3().setFromObject(group);
      const center = box.getCenter(new THREE.Vector3());
      const minY = box.min.y;

      // Center model horizontally and ground it at y=0
      group.position.x = -center.x;
      group.position.z = -center.z;
      group.position.y = -minY;

      onLoaded();
    });
  }, [fbx, onLoaded]);

  // Update animation on every frame using clock delta
  useFrame((_, delta) => {
    if (mixerRef.current) {
      mixerRef.current.update(delta);
    }
  });

  const scale = model.scale ?? 1;
  const rotation = model.rotation ?? [0, 0, 0];

  return (
    <group ref={groupRef}>
      <primitive
        object={fbx}
        scale={scale}
        rotation={rotation}
      />
    </group>
  );
}

interface ModelLoaderProps {
  model: Model3D;
  onLoaded: () => void;
}

function ModelLoader({ model, onLoaded }: ModelLoaderProps) {
  if (model.format === 'glb' || model.format === 'gltf') {
    return <GLTFModel model={model} onLoaded={onLoaded} />;
  }

  if (model.format === 'fbx') {
    return <FBXModel model={model} onLoaded={onLoaded} />;
  }

  return null;
}

function Loader() {
  return (
    <Html center>
      <div
        style={{
          color: '#05d9e8',
          fontFamily: 'monospace',
          fontSize: '1rem',
          textShadow: '0 0 10px rgba(5, 217, 232, 0.8)',
        }}
      >
        Cargando modelo...
      </div>
    </Html>
  );
}

export interface LightingSettings {
  exposure: number;
  environmentIntensity: number;
  environmentPreset: 'sunset' | 'dawn' | 'night' | 'warehouse' | 'forest' | 'apartment' | 'studio' | 'city' | 'park' | 'lobby';
  environmentRotation: number;
  showShadows: boolean;
  shadowIntensity: number;
  shadowBlur: number;
  // Analytical lights
  keyLightIntensity: number;
  keyLightColor: string;
  fillLightIntensity: number;
  rimLightIntensity: number;
}

interface SceneSetupProps {
  settings: LightingSettings;
}

function SceneSetup({ settings }: SceneSetupProps) {
  const { gl } = useThree();

  useEffect(() => {
    // Set tone mapping exposure for HDR
    gl.toneMappingExposure = settings.exposure;
  }, [gl, settings.exposure]);

  return null;
}

interface LightingRigProps {
  settings: LightingSettings;
}

function LightingRig({ settings }: LightingRigProps) {
  const keyLightColor = new THREE.Color(settings.keyLightColor);

  return (
    <>
      {/* HDR Environment for Image-Based Lighting (IBL) - This is the key to realistic lighting */}
      <Environment
        preset={settings.environmentPreset}
        background={false}
        environmentIntensity={settings.environmentIntensity}
        environmentRotation={[0, settings.environmentRotation, 0]}
      />

      {/* Key Light - Main directional light with shadows */}
      <directionalLight
        position={[5, 8, 3]}
        intensity={settings.keyLightIntensity}
        color={keyLightColor}
        castShadow
        shadow-mapSize={[2048, 2048]}
        shadow-camera-near={0.1}
        shadow-camera-far={50}
        shadow-camera-left={-10}
        shadow-camera-right={10}
        shadow-camera-top={10}
        shadow-camera-bottom={-10}
        shadow-bias={-0.0001}
        shadow-normalBias={0.02}
      />

      {/* Fill Light - Softer light from opposite side */}
      <directionalLight
        position={[-4, 4, -2]}
        intensity={settings.fillLightIntensity}
        color="#e8f4ff"
      />

      {/* Rim Light - Back light for edge definition */}
      <spotLight
        position={[-2, 6, -6]}
        intensity={settings.rimLightIntensity}
        angle={0.6}
        penumbra={0.8}
        color="#ffd4e8"
        castShadow={false}
      />

      {/* Ambient fill to prevent pure black shadows */}
      <ambientLight intensity={0.15} color="#ffffff" />

      {/* Ground contact shadows for realism */}
      {settings.showShadows && (
        <ContactShadows
          position={[0, 0.01, 0]}
          opacity={settings.shadowIntensity}
          scale={12}
          blur={settings.shadowBlur}
          far={6}
          resolution={512}
          color="#000000"
        />
      )}

      {/* Ground plane to receive shadows */}
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, 0, 0]}
        receiveShadow
      >
        <planeGeometry args={[50, 50]} />
        <shadowMaterial transparent opacity={settings.shadowIntensity * 0.3} />
      </mesh>
    </>
  );
}

interface ModelSceneProps {
  model: Model3D;
  autoRotate?: boolean;
  onLoaded: () => void;
  lightingSettings: LightingSettings;
  touchPanMode?: boolean;
}

export function ModelScene({
  model,
  autoRotate = true,
  onLoaded,
  lightingSettings,
  touchPanMode = false,
}: ModelSceneProps) {
  return (
    <Canvas
      camera={{ position: [0, 1.5, 5], fov: 45 }}
      gl={{
        alpha: true,
        antialias: true,
        toneMapping: THREE.ACESFilmicToneMapping,
        toneMappingExposure: lightingSettings.exposure,
        outputColorSpace: THREE.SRGBColorSpace,
      }}
      shadows="soft"
      style={{ background: 'transparent' }}
      dpr={[1, 2]}
    >
      <SceneSetup settings={lightingSettings} />
      <LightingRig settings={lightingSettings} />

      <Bounds fit clip margin={1.2}>
        <Suspense fallback={<Loader />}>
          <ModelLoader model={model} onLoaded={onLoaded} />
        </Suspense>
      </Bounds>

      <OrbitControls
        makeDefault
        autoRotate={autoRotate}
        autoRotateSpeed={1.5}
        enablePan={true}
        panSpeed={0.8}
        minDistance={0.5}
        maxDistance={20}
        minPolarAngle={0}
        maxPolarAngle={Math.PI / 1.8}
        enableDamping
        dampingFactor={0.05}
        mouseButtons={{
          LEFT: THREE.MOUSE.ROTATE,
          MIDDLE: THREE.MOUSE.DOLLY,
          RIGHT: THREE.MOUSE.PAN,
        }}
        touches={
          touchPanMode
            ? { ONE: THREE.TOUCH.PAN, TWO: THREE.TOUCH.DOLLY_ROTATE }
            : { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN }
        }
      />
    </Canvas>
  );
}

export const defaultLightingSettings: LightingSettings = {
  exposure: 1.0,
  environmentIntensity: 1.0,
  environmentPreset: 'studio',
  environmentRotation: 0,
  showShadows: true,
  shadowIntensity: 0.6,
  shadowBlur: 2.5,
  keyLightIntensity: 1.2,
  keyLightColor: '#ffffff',
  fillLightIntensity: 0.4,
  rimLightIntensity: 0.8,
};
